var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DefaultButton, MessageBar, MessageBarButton, MessageBarType, PrimaryButton, ProgressIndicator, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import React, { useCallback, useMemo, useState } from "react";
import { useClasses } from "../../utils/useClasses";
import { Text } from "../texts/Text";
var getClasses = function (theme) { return ({
    container: {
        backgroundColor: theme.palette.white,
    },
    info: {
        paddingBottom: 8,
    },
    primaryButton: {}
}); };
var tokens = {
    container: { childrenGap: 16 },
    buttons: { childrenGap: 12 }
};
function toMessageBarType(type, def) {
    switch (type) {
        case FeedbackType.SUCCESS: return MessageBarType.success;
        case FeedbackType.ERROR: return MessageBarType.error;
        case FeedbackType.INFO: return MessageBarType.info;
    }
    return def;
}
export var FormProgressControl = function (props) {
    var className = props.className, sectionCount = props.sectionCount, currentPos = props.currentPos, status = props.status, onNext = props.onNext, onPrevious = props.onPrev, onSubmit = props.onSubmit, onResubmit = props.onResubmit, info = props.info, successMessage = props.successMessage, errorMessage = props.errorMessage, _progressLabel = props.progressLabel, _nextText = props.nextText, _previousText = props.prevText, _submitText = props.submitText, disableNext = props.disableNext, feedback = props.feedback, hideProgressIndicator = props.hideProgressIndicator, forwardProps = __rest(props, ["className", "sectionCount", "currentPos", "status", "onNext", "onPrev", "onSubmit", "onResubmit", "info", "successMessage", "errorMessage", "progressLabel", "nextText", "prevText", "submitText", "disableNext", "feedback", "hideProgressIndicator"]);
    var isFinalSection = currentPos === sectionCount - 1;
    var progressLabel = _progressLabel || "Progress";
    var nextText = _nextText || "Next";
    var prevText = _previousText || "Previous";
    var submitText = _submitText || "Submit";
    var classes = useClasses(getClasses);
    var _a = useState(0), submitAttempt = _a[0], setSubmitAttempt = _a[1];
    var onPerformSubmit = function () {
        setSubmitAttempt(1);
    };
    var performSubmit = useCallback(function () {
        if (onSubmit)
            onSubmit();
        else
            onNext === null || onNext === void 0 ? void 0 : onNext();
        onPerformSubmit();
    }, [onNext, onSubmit]);
    var onNextClicked = useCallback(function () {
        if (isFinalSection) {
            if (submitAttempt === 0) {
                performSubmit();
            }
            else {
                if (onResubmit) {
                    onResubmit();
                    onPerformSubmit();
                }
                else {
                    performSubmit();
                }
            }
            return;
        }
        onNext === null || onNext === void 0 ? void 0 : onNext();
    }, [isFinalSection, onNext, onResubmit, performSubmit, submitAttempt]);
    var primaryButton = useMemo(function () {
        return _jsx(PrimaryButton, { className: classes.primaryButton, text: isFinalSection ? submitText : nextText, disabled: disableNext !== undefined ? disableNext : status === FormStatus.PROCESSING || ((status === FormStatus.SUBMITTING || status === FormStatus.SUBMITTED) && isFinalSection), onClick: onNextClicked }, void 0);
    }, [classes.primaryButton, onNextClicked, status, disableNext, isFinalSection, submitText, nextText]);
    var secondaryButton = useMemo(function () {
        if (currentPos === 0)
            return null;
        return _jsx(DefaultButton, { text: prevText, onClick: onPrevious }, void 0);
    }, [currentPos, prevText, onPrevious]);
    var spinner = useMemo(function () {
        if (status === FormStatus.SUBMITTING || status === FormStatus.PROCESSING)
            return _jsx(Spinner, { size: SpinnerSize.medium }, void 0);
        return null;
    }, [status]);
    var message = useMemo(function () {
        if (feedback) {
            if (React.isValidElement(feedback))
                return feedback;
            var fb = feedback;
            return _jsx(MessageBar, __assign({ messageBarType: toMessageBarType(fb.type), actions: fb.actions
                    ? Array.isArray(fb.actions)
                        ? _jsx("div", { children: fb.actions.map(function (action) { return React.isValidElement(action)
                                ? action
                                : _jsx(MessageBarButton, __assign({ onClick: action.onClick }, { children: action.text }), action.key); }) }, void 0)
                        : fb.actions
                    : undefined }, { children: fb.message }), void 0);
        }
        if (errorMessage)
            return _jsx(MessageBar, __assign({ messageBarType: MessageBarType.error }, { children: errorMessage }), void 0);
        if (successMessage)
            return _jsx(MessageBar, __assign({ messageBarType: MessageBarType.success }, { children: successMessage }), void 0);
    }, [feedback, errorMessage, successMessage]);
    return _jsxs(Stack, __assign({ className: classes.container + " " + className, tokens: tokens.container }, forwardProps, { children: [info && _jsx(Text, __assign({ className: classes.info }, { children: info }), void 0), _jsxs(Stack, __assign({ tokens: tokens.buttons, horizontal: true, horizontalAlign: "start" }, { children: [secondaryButton, primaryButton, spinner] }), void 0), message, !hideProgressIndicator && _jsx(ProgressIndicator, { label: progressLabel, barHeight: 4, percentComplete: status === FormStatus.SUBMITTED || status === FormStatus.SUBMITTING ? 1 : (currentPos + 1) / sectionCount }, void 0)] }), void 0);
};
export var FormStatus;
(function (FormStatus) {
    FormStatus["NONE"] = "none";
    FormStatus["SUBMITTING"] = "submitting";
    FormStatus["PROCESSING"] = "processing";
    FormStatus["SUBMITTED"] = "submitted";
})(FormStatus || (FormStatus = {}));
export var FeedbackType;
(function (FeedbackType) {
    FeedbackType[FeedbackType["SUCCESS"] = 4] = "SUCCESS";
    FeedbackType[FeedbackType["ERROR"] = 1] = "ERROR";
    FeedbackType[FeedbackType["INFO"] = 0] = "INFO";
})(FeedbackType || (FeedbackType = {}));
//# sourceMappingURL=FormProgressControl.js.map